// External
import join from 'url-join';

// Internal
import http from '../../util/http';
import { createAction, asyncAction } from '../../util/actions';

// Functions
import { handleError } from '../util';
import { getType } from '../../util/lodash';
import { flatten } from '../../util/array/flatten';

const api2 = join(import.meta.env.VITE_API2_URL, 'v1');

export const setCatalogCatalogTypes = createAction('SET_CATALOG_CATALOG_TYPES');

export const fetchCatalogById = asyncAction({
  baseName: 'FETCH_CATALOG_BY_ID',
  action: action => catalogId => dispatch => {
    dispatch(action.start());

    return http
      .get(join(api2, `/catalogs/${catalogId}`))
      .then(({ data }) => dispatch(action.success({ catalog: data })))
      .catch(error => handleError(dispatch, action, error));
  }
});

export const fetchSeasons = asyncAction({
  baseName: 'FETCH_SEASONS',
  action: action => () => dispatch => {
    dispatch(action.start());

    return http
      .get(join(api2, '/seasons'))
      .then(({ data }) => dispatch(action.success({ seasons: data.objects })))
      .catch(error => handleError(dispatch, action, error));
  }
});

export const findProducts = async payload =>
  http
    .post(join(api2, '/products'), payload)
    .then(({ data }) => data?.objects ?? [])
    .catch(() => []);

/**
 * @name addCatalogProducts
 * @async
 * @desc request update to products in catalog
 * @param {object} params
 * @param {string} catalogId the id of the catalog to be updated
 * @param {array} productCodes the list of products to add to the catalog
 * @param {array} [products] optional per-product returnability: [{ productCode, returnable? }]
 */

export const addCatalogProducts = asyncAction({
  baseName: 'ADD_CATALOG_PRODUCTS',
  action:
    action =>
    ({ catalogId, productCodes, products }) =>
    dispatch => {
      dispatch(action.start());

      const payload = products ? { productCodes, products } : { productCodes };

      return http
        .post(join(api2, `/catalogs/${catalogId}/addProduct`), payload)
        .then(async () => {
          return dispatch(action.success());
        })
        .catch(error => handleError(dispatch, action, error));
    }
});

/**
 * @name indexCatalog
 * @async
 * @desc index the catalog by id
 * @param {string} catalogId the catalog ID to index
 */
export const indexCatalog = asyncAction({
  baseName: 'INDEX_CATALOG',
  action: action => catalogId => dispatch => {
    dispatch(action.start());

    return http
      .post(join(api2, `/catalogs/${catalogId}/index`))
      .then(async () => {
        await dispatch(fetchCatalogById(catalogId));

        return dispatch(action.success(catalogId));
      })
      .catch(error => handleError(dispatch, action, error));
  }
});

/**
 * @name removeCatalogProduct
 * @async
 * @desc request removes product from catalog
 * @param {object} params
 * @param {string} catalogId the id of the catalog to be updated
 * @param {object} product the product to add to the catalog
 */
export const removeCatalogProduct = asyncAction({
  baseName: 'REMOVE_CATALOG_PRODUCT',
  action:
    action =>
    ({ catalogId, productCode }) =>
    dispatch => {
      dispatch(action.start());

      return http
        .post(join(api2, `/catalogs/${catalogId}/removeProduct`), { productCode })
        .then(async () => {
          return dispatch(action.success());
        })
        .catch(error => handleError(dispatch, action, error));
    }
});

/**
 * @name updateCatalogCatalogTypes
 * @async
 * @desc request update to catalogTypes in catalog
 * @param {object} params
 * @param {string} catalogId the id of the catalog to be updated
 * @param {object} catalogTypes the list of catalogTypes to add to the catalog
 */
export const updateCatalogCatalogTypes = asyncAction({
  baseName: 'UPDATE_CATALOG_CATALOG_TYPES',
  action:
    action =>
    ({ catalogId, catalogTypeIds }) =>
    dispatch => {
      dispatch(action.start());

      const payload = { catalogTypes: catalogTypeIds.map(id => ({ id })) };

      return http
        .post(join(api2, `/catalogs/${catalogId}/catalogTypes`), payload)
        .then(() => dispatch(action.success()))
        .catch(error => handleError(dispatch, action, error));
    }
});

export const saveCatalogCatalogTypes =
  ({ catalogId, catalogTypeIds }) =>
  async dispatch => {
    const event = await dispatch(updateCatalogCatalogTypes({ catalogId, catalogTypeIds }));

    if (event && event.type === updateCatalogCatalogTypes.event.success) {
      return dispatch(setCatalogCatalogTypes({ catalogId, catalogTypeIds }));
    }

    return event && event.type === updateCatalogCatalogTypes.event.success;
  };

/**
 * @name createCatalog
 * @async
 * @desc create a new catalog
 * @param {object} params
 * @param {object} catalogData the new catalog's metadata
 * @param {array} products the list of products to associate with this new catalog
 * @param {array} catalogTypes the list of catalogTypes to associate with this new catalog
 */
export const createCatalog = asyncAction({
  baseName: 'CREATE_CATALOG',
  action:
    action =>
    ({ catalogData }) =>
    dispatch => {
      dispatch(action.start());

      return http
        .post(join(api2, '/catalogs'), catalogData)
        .then(({ data: response }) => {
          dispatch(action.success());

          const catalogId = response.id;

          return catalogId;
        })
        .catch(error => handleError(dispatch, action, error));
    }
});

/**
 * @name updateCatalog
 * @async
 * @desc request update to catalog
 * @param {object} params
 * @param {string} catalogId the catalog ID to update
 * @param {object} catalogData the catalog metadata
 */
export const updateCatalog = asyncAction({
  baseName: 'UPDATE_CATALOG',
  action:
    action =>
    ({ catalogId, catalogData }) =>
    dispatch => {
      dispatch(action.start());

      return http
        .patch(join(api2, `/catalogs/${catalogId}`), catalogData)
        .then(() => dispatch(action.success()))
        .catch(error => handleError(dispatch, action, error));
    }
});

/**
 * @name saveCatalog
 * @async
 * @desc request to create and/or update a catalog
 * @param {object} params
 * @param {string} catalogId the catalog ID to update
 * @param {object} payload the user input values provided by Formik
 */
export const saveCatalog =
  ({ catalogId, payload }) =>
  async dispatch => {
    const {
      atOnceEnd,
      atOnceStart,
      catalogTypes,
      directShip,
      forecastCrd,
      forecastStart,
      forecastEnd,
      futuresCrd,
      futuresStart,
      futuresEnd,
      groupName,
      name,
      orderTypes,
      ownerId,
      salesOrg,
      seasons
    } = payload;
    const catalogData = {
      atOnceEnd,
      atOnceStart,
      directShip,
      forecastCrd,
      forecastStart,
      forecastEnd,
      futuresCrd,
      futuresStart,
      futuresEnd,
      groupName,
      name,
      orderTypes,
      ownerId,
      salesOrg,
      seasons
    };

    if (!catalogId) {
      // Create catalog
      const newCatalogId = await dispatch(createCatalog({ catalogData }));

      // If createCatalog fails, newCatalogId is an object
      if (typeof newCatalogId === 'string') {
        await dispatch(saveCatalogCatalogTypes({ catalogId: newCatalogId, catalogTypeIds: catalogTypes }));
        await dispatch(fetchCatalogById(newCatalogId));

        return newCatalogId;
      }

      return null;
    }

    // Update catalog
    // eslint-disable-next-line max-len
    await dispatch(saveCatalogCatalogTypes({ catalogId, catalogTypeIds: [] })); // Necessary for instance where salesOrg changes
    await dispatch(updateCatalog({ catalogId, catalogData }));
    await dispatch(saveCatalogCatalogTypes({ catalogId, catalogTypeIds: catalogTypes }));
    await dispatch(fetchCatalogById(catalogId));

    return null;
  };

/**
 * @name unindexCatalog
 * @async
 * @desc unindex the catalog by id
 * @param {string} catalogId the catalog ID to unindex
 */
export const unindexCatalog = asyncAction({
  baseName: 'UNINDEX_CATALOG',
  action: action => catalogId => dispatch => {
    dispatch(action.start());

    return http
      .post(join(api2, `/catalogs/${catalogId}/unindex`))
      .then(async () => {
        await dispatch(fetchCatalogById(catalogId));

        return dispatch(action.success(catalogId));
      })
      .catch(error => handleError(dispatch, action, error));
  }
});

/**
 * @name generateValidationReport
 * @async
 * @desc Generates the Invalid Products Validation Report
 * @param {object} catalogs The catalogs to be included in the report
 */
export const generateValidationReport = asyncAction({
  baseName: 'GENERATE_VALIDATION_REPORT',
  action: action => catalogs => dispatch => {
    dispatch(action.start());

    return http
      .post(
        join(api2, '/reports/generateInvalidProductsReport'),
        {
          catalogs: getType(catalogs) === 'array' ? catalogs : Object.keys(catalogs)
        },
        { 'Content-Type': 'application/json' }
      )
      .then(response => response.blob())
      .then(blob => {
        dispatch(action.success(blob));

        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);

        link.href = url;
        link.download = 'ValidationReport.csv';

        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      })
      .catch(error => handleError(dispatch, action, error));
  }
});

/**
 * @function getReportStatusAndUrl
 * Polls report status until COMPLETE (returns presigned downloadUrl) or rejects on FAILED / missing URL.
 */
export const getReportStatusAndUrl = async id =>
  new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      http
        .get(join(api2, `/reports/status/${id}`))
        .then(({ data }) => {
          const { reportStatus, downloadUrl, errorMessage } = data;

          if (reportStatus === 'IN PROGRESS') {
            return;
          }

          clearInterval(interval);

          if (reportStatus === 'FAILED' || !downloadUrl) {
            reject(new Error(errorMessage || 'Failed to generate report'));
            return;
          }

          resolve(downloadUrl);
        })
        .catch(error => {
          clearInterval(interval);
          reject(error);
        });
    }, 2500);
  });

/**
 * @function saveFindProductFormState
 * @param {object} params
 * @param {object} params.formState Formik state values for the FindProduct form.
 * Used to save the state of the form when navigating.
 */
export const saveFindProductFormState = createAction('SAVE_FIND_PRODUCT_FORM_STATE');

export const fetchAllCatalogs = async ({ totalResources, sortQueryParams, filterQueryParams }) => {
  const url = join(api2, `catalogs/?count=100${sortQueryParams}${filterQueryParams}`);
  const numberOfRequests = Math.ceil(totalResources / 100);

  const subsequentRequests = [];
  let anchor = 0;
  /* eslint-disable no-await-in-loop */
  for (let i = 0; i < numberOfRequests; i += 6) {
    const subsequestRequestsGroup = [];
    const limit = Math.min(6, numberOfRequests - i);
    for (let j = 0; j < limit; j++) {
      subsequestRequestsGroup.push(http.get(`${url}&anchor=${anchor}`));
      anchor += 100;
    }

    await Promise.all(subsequestRequestsGroup)
      .then(responses => responses.map(r => r.data.objects))
      .then(result => subsequentRequests.push(flatten(result)));
  }
  return flatten(subsequentRequests);
};

export const fetchAllCatalogProducts = async ({ catalogId, totalResources, sortQueryParams, filterQueryParams }) => {
  const url = join(api2, `catalogs/${catalogId}/products?count=100${sortQueryParams}${filterQueryParams}`);
  const numberOfRequests = Math.ceil(totalResources / 100);

  const subsequentRequests = [];
  let anchor = 0;
  /* eslint-disable no-await-in-loop */
  for (let i = 0; i < numberOfRequests; i += 6) {
    const subsequestRequestsGroup = [];
    const limit = Math.min(6, numberOfRequests - i);
    for (let j = 0; j < limit; j++) {
      subsequestRequestsGroup.push(http.get(`${url}&anchor=${anchor}`));
      anchor += 100;
    }

    await Promise.all(subsequestRequestsGroup)
      .then(responses => responses.map(r => r.data.objects))
      .then(result => subsequentRequests.push(flatten(result)));
  }
  return flatten(subsequentRequests);
};

/**
 * @name deleteCatalog
 * @async
 * @desc delete catalog by id
 * @param {string} catalogId the catalog ID to delete
 */
export const deleteCatalog = catalogId => {
  return http.delete(join(api2, `/catalogs/${catalogId}`));
};

export const deleteAllCatalogs = filterQueryParams => {
  return http.delete(join(api2, `/catalogs${filterQueryParams ? `?${filterQueryParams.replace('&', '')}` : ''}`));
};

export const removeAllCatalogProducts = (catalogId, filterQueryParams) => {
  return http.delete(
    join(api2, `/catalogs/${catalogId}/products${filterQueryParams ? `?${filterQueryParams.replace('&', '')}` : ''}`)
  );
};

export default {
  createCatalog,
  generateValidationReport,
  updateCatalog,
  updateCatalogCatalogTypes,
  saveCatalog,
  unindexCatalog,
  saveFindProductFormState,
  deleteCatalog,
  deleteAllCatalogs,
  removeAllCatalogProducts
};
